// High-intent JP card landings (#752).
// Facts are copied from api/db/fixtures (items.csv, item_data.json, tags.csv).
// Intros are written per card. Do not mass-generate a shared paragraph.
// Icons flagged editorial are the #719 cluster; there is no public article URL yet.
// ブラッキー SAR is not in the catalog, so it is not given a page.

export type CatalogAttack = Readonly<{
  name: string
  damage: string | null
  text: string | null
}>

export type CatalogAbility = Readonly<{
  name: string
  text: string
}>

export type CatalogLink = Readonly<{
  id: number
  nameJa: string
}>

export type PriorityGroup = 'classic' | 'promo' | 'market'

export type PriorityCard = Readonly<{
  slug: string
  itemId: number
  categoryId: number
  nameJa: string
  nameEn: string
  h1: string
  title: string
  description: string
  intro: string
  set: CatalogLink
  printing?: CatalogLink
  series?: CatalogLink
  alsoIn?: readonly CatalogLink[]
  releaseDate?: string
  setReleaseDate?: string
  rarityJa?: string
  hp?: string
  level?: string
  typesJa: readonly string[]
  stageJa?: string
  artist?: string
  artistEn?: string
  attacks: readonly CatalogAttack[]
  abilities: readonly CatalogAbility[]
  flavorText?: string
  editorial: boolean
  group: PriorityGroup
  relatedSlugs: readonly string[]
}>

const fireVortex: CatalogAttack = {
  name: 'ほのおのうず',
  damage: '100',
  text: '自分のエネルギーカードを2枚はがしてすてる。',
}

const energyBurn: CatalogAbility = {
  name: 'エナジーバーン',
  text: 'リザードンは、「炎」以外の自分のエネルギーを「炎」エネルギーとして扱うことができる。「こんらん」状態のときには使えない。',
}

export const HONESTY_SENTENCE = 'HOLICは店舗の買取ではなく、出品者が価格を決めて販売するマーケットです。'

export const PRIORITY_CARDS: readonly PriorityCard[] = [
  {
    slug: 'base-charizard-unlimited',
    itemId: 21,
    categoryId: 4,
    nameJa: 'リザードン',
    nameEn: 'Charizard',
    h1: 'リザードン（旧裏面）',
    title: 'リザードン（旧裏面・第1弾拡張パック）の販売 | HOLIC',
    description: '旧裏面のリザードン（第1弾拡張パック、レア、HP120、レベル76）。イラストは有田満弘、特性はエナジーバーン、ワザはほのおのうず。出品者の販売価格を比べるページです。',
    intro: '1996年10月15日発売の第1弾拡張パック、旧裏面のリザードンです。HPは120、レベルは76、レア。有田満弘のイラストで、特性はエナジーバーン、ワザはダメージ100のほのおのうずです。買取店の査定を探している場合でも、ここで見えるのは出品者が自分で付けた販売価格だけです。',
    set: { id: 3, nameJa: '第1弾拡張パック' },
    printing: { id: 4, nameJa: '旧裏面' },
    series: { id: 2, nameJa: '1番目のシリーズ' },
    releaseDate: '1996-10-15',
    rarityJa: 'レア',
    hp: '120',
    level: '76',
    typesJa: ['炎'],
    stageJa: '2進化',
    artist: '有田満弘',
    artistEn: 'Mitsuhiro Arita',
    attacks: [fireVortex],
    abilities: [energyBurn],
    flavorText: '地上1400メートルまで羽をつかって飛ぶことができる。高熱の炎をはく。',
    editorial: true,
    group: 'classic',
    relatedSlugs: ['base-charizard-1st', 'gym-blaines-charizard', 'shining-charizard'],
  },
  {
    slug: 'base-charizard-1st',
    itemId: 120,
    categoryId: 5,
    nameJa: 'リザードン',
    nameEn: 'Charizard',
    h1: 'リザードン（初版）',
    title: 'リザードン（初版・第1弾拡張パック）の販売 | HOLIC',
    description: '初版（マークなし）の第1弾リザードン。HP120、レベル76、炎タイプの2進化。有田満弘のイラストで、ワザはほのおのうず。旧裏面とは別レコードの販売ページです。',
    intro: 'マークのない初版としてカタログにある第1弾のリザードンです。旧裏面の再版とは印刷が区別され、同じ有田満弘・ほのおのうず・エナジーバーンでも別カードとして扱います。HP120、レベル76、進化は2進化の炎タイプです。',
    set: { id: 3, nameJa: '第1弾拡張パック' },
    printing: { id: 5, nameJa: '初版' },
    series: { id: 2, nameJa: '1番目のシリーズ' },
    setReleaseDate: '1996-10-15',
    rarityJa: 'レア',
    hp: '120',
    level: '76',
    typesJa: ['炎'],
    stageJa: '2進化',
    artist: '有田満弘',
    artistEn: 'Mitsuhiro Arita',
    attacks: [fireVortex],
    abilities: [energyBurn],
    flavorText: '地上1400メートルまで羽をつかって飛ぶことができる。高熱の炎をはく。',
    editorial: true,
    group: 'classic',
    relatedSlugs: ['base-charizard-unlimited', 'base-mewtwo-1st'],
  },
  {
    slug: 'shining-charizard',
    itemId: 694,
    categoryId: 2005,
    nameJa: 'ひかるリザードン',
    nameEn: 'Shining Charizard',
    h1: 'ひかるリザードン',
    title: 'ひかるリザードン（闇、そして光へ...）の販売 | HOLIC',
    description: 'ひかるリザードンは『闇、そして光へ...』のレア（☆☆☆）、HP100、レベル61。イラストはHironobu Yoshida、ワザはしゃくねつのほのお。販売出品を見るページです。',
    intro: '拡張パック第4弾『闇、そして光へ...』のひかるリザードンは、レアリティがレア（☆☆☆）のひかるポケモンです。HP100、レベル61。イラストレーターはHironobu Yoshida、ワザはしゃくねつのほのお（ダメージ100）だけです。',
    set: { id: 2005, nameJa: '拡張パック第4弾 闇、そして光へ...' },
    series: { id: 1910, nameJa: 'ポケモンカード★neo' },
    setReleaseDate: '2001-03-09',
    rarityJa: 'レア（☆☆☆）',
    hp: '100',
    level: '61',
    typesJa: ['炎'],
    stageJa: 'たね',
    artist: 'Hironobu Yoshida',
    artistEn: 'Hironobu Yoshida',
    attacks: [{
      name: 'しゃくねつのほのお',
      damage: '100',
      text: '自分についている「炎」「雷」の「エネルギーカード」を、それぞれ1枚ずつトラッシュする。その後、コインを投げて「うら」なら、自分にも30ダメージ。(トラッシュできないとき、このワザのダメージは、すべてなくなる。)',
    }],
    abilities: [],
    flavorText: '何でも溶かしてしまうほどのしゃくねつの炎を吹き出し、相手を苦しめる。',
    editorial: true,
    group: 'market',
    relatedSlugs: ['base-charizard-unlimited', 'neo-umbreon'],
  },
  {
    slug: 'dark-charizard',
    itemId: 349,
    categoryId: 1925,
    nameJa: 'わるいリザードン',
    nameEn: 'Dark Charizard',
    h1: 'わるいリザードン',
    title: 'わるいリザードン（ロケット団）の販売 | HOLIC',
    description: 'ロケット団のわるいリザードン。HP80、レベル38、炎の2進化で、イラストは杉森建。ワザはつめではじくとれんぞくファイヤーボール。出品者の販売価格を掲載します。',
    intro: 'ロケット団の拡張、第4弾に入っているわるいリザードンです。レベル38でHPは80まで下がり、杉森建が描いています。ワザはつめではじくと、エネルギーの枚数で増えるれんぞくファイヤーボールです。',
    set: { id: 1925, nameJa: '第4弾拡張パック ロケット団' },
    series: { id: 2, nameJa: '1番目のシリーズ' },
    setReleaseDate: '1997-11-21',
    rarityJa: 'レア',
    hp: '80',
    level: '38',
    typesJa: ['炎'],
    stageJa: '2進化',
    artist: '杉森建',
    artistEn: 'Ken Sugimori',
    attacks: [
      { name: 'つめではじく', damage: '10', text: null },
      {
        name: 'れんぞくファイヤーボール',
        damage: '50×',
        text: 'このカードについている「炎」エネルギーカードの数だけコインを投げ、「おもて」の数×50のダメージ。その後、「おもて」の数ぶんの「炎」エネルギーカードを、このカードからはがしてトラッシュする。(トラッシュできない時、このワザのダメージは0になる)',
      },
    ],
    abilities: [],
    flavorText: 'とりつかれたように炎の塊を吐き出して、あらゆるものを焼きつくそうとする。',
    editorial: false,
    group: 'classic',
    relatedSlugs: ['gym-blaines-charizard', 'base-charizard-unlimited'],
  },
  {
    slug: 'promo-charizard-song',
    itemId: 214,
    categoryId: 45,
    nameJa: 'リザードン',
    nameEn: 'Charizard',
    h1: 'リザードン（ポケモン♪ベストコレクション）',
    title: 'リザードン（ポケモン♪ベストコレクション）の販売 | HOLIC',
    description: 'ポケモン♪ベストコレクションのプロモ・リザードン。杉森建、HP120、レベル76、ワザはほのおのうず、特性はエナジーバーン。とりかえっこプリーズ区分にも載るカードです。',
    intro: 'プロモのリザードンで、収録先は『みんなで選んだポケモンソング&ポケモンカード～ポケモン♪ベストコレクション』です。カタログ上はとりかえっこプリーズ側の区分にも紐づきます。杉森建、HP120、レベル76、ワザはほのおのうず、特性はエナジーバーンです。',
    set: { id: 45, nameJa: 'みんなで選んだポケモンソング&ポケモンカード～ポケモン♪ベストコレクション' },
    series: { id: 42, nameJa: 'プロモーションカード' },
    alsoIn: [{ id: 43, nameJa: 'とりかえっこプリーズ' }],
    setReleaseDate: '1999-01-01',
    rarityJa: 'レア',
    hp: '120',
    level: '76',
    typesJa: ['炎'],
    stageJa: '2進化',
    artist: '杉森建',
    artistEn: 'Ken Sugimori',
    attacks: [{
      name: 'ほのおのうず',
      damage: '100',
      text: 'このカードについているエネルギーカードを2枚トラッシュする。(トラッシュできない時、このワザのダメージは0になる)',
    }],
    abilities: [{
      name: 'エナジーバーン',
      text: 'この力は自分の番の中で何度でも使える。このカードについているすべてのエネルギーは、「炎」エネルギーになり、自分の番の終わりに元にもどる。このカードが「ねむり・マヒ・こんらん」状態の時、この力は消える。',
    }],
    flavorText: '地上1400メートルまで翼をつかって飛ぶことができる。高熱の炎をはく。',
    editorial: false,
    group: 'promo',
    relatedSlugs: ['base-charizard-unlimited', 'promo-pikachu-toyota'],
  },
  {
    slug: 'promo-pikachu-summer',
    itemId: 947,
    categoryId: 2055,
    nameJa: 'ピカチュウのなつやすみ',
    nameEn: "Pikachu's Summer Vacation",
    h1: 'ピカチュウのなつやすみ',
    title: 'ピカチュウのなつやすみ（コロコロ1998年6月号）の販売 | HOLIC',
    description: 'コロコロコミック1998年6月号のプロモ、ピカチュウのなつやすみ。HP50の雷タイプで、イラストはAtsuko Nishida、ワザはみんなであそぼ。通常のピカチュウとは別カードです。',
    intro: '月刊コロコロコミック1998年6月号のプロモが、ピカチュウのなつやすみです。HP50、タイプは雷。イラストレーターはAtsuko Nishida、ワザの名前はみんなであそぼです。拡張パックの通常ピカチュウとはカード名から別物です。',
    set: { id: 2055, nameJa: '月刊コロコロコミック98年6月号' },
    series: { id: 1937, nameJa: '月刊コロコロコミック' },
    setReleaseDate: '1998-06-15',
    hp: '50',
    typesJa: ['雷'],
    stageJa: 'たね',
    artist: 'Atsuko Nishida',
    artistEn: 'Atsuko Nishida',
    attacks: [{
      name: 'みんなであそぼ',
      damage: null,
      text: '自分の山札から、好きなだけ「たねポケモン」カードを選び出し、自分のベンチに出してよい。その後、その山札をよく切る。',
    }],
    abilities: [],
    flavorText: '夏休みのポケモンたちは、みんななかよし。ピカチュウといっしょにみんなで楽しく遊ぶ。',
    editorial: false,
    group: 'promo',
    relatedSlugs: ['promo-pikachu-toyota', 'web-surfing-pikachu'],
  },
  {
    slug: 'promo-pikachu-toyota',
    itemId: 220,
    categoryId: 1944,
    nameJa: 'ピカチュウ',
    nameEn: 'Pikachu',
    h1: 'ピカチュウLV.16（トヨタオートキャンペーン）',
    title: 'ピカチュウLV.16（トヨタオートキャンペーン）の販売 | HOLIC',
    description: 'トヨタオートキャンペーンのプロモ、ピカチュウLV.16。HP60、杉森建のイラストで、ワザはなきごえとでんきショック。第1弾のLV.12とは別の販売ページです。',
    intro: 'トヨタオートキャンペーンのプロモ、ピカチュウLV.16です。HPは60。杉森建のイラストで、ワザはなきごえとでんきショックの二つです。第1弾の通常版（LV.12、ワザはかじるとでんげき）とはレベルも技も違います。',
    set: { id: 1944, nameJa: 'トヨタオートキャンペーン' },
    series: { id: 42, nameJa: 'プロモーションカード' },
    setReleaseDate: '1997-01-01',
    hp: '60',
    level: '16',
    typesJa: ['雷'],
    stageJa: 'たね',
    artist: '杉森建',
    artistEn: 'Ken Sugimori',
    attacks: [
      { name: 'なきごえ', damage: null, text: '次の相手の番でうけるダメージを10へらすことができる。' },
      { name: 'でんきショック', damage: '20', text: 'コインを投げて「おもて」なら、相手を「マヒ」状態にする。' },
    ],
    abilities: [],
    flavorText: 'ほっぺたの両側に小さい電気袋をもつ。ピンチのときに放電する。',
    editorial: false,
    group: 'promo',
    relatedSlugs: ['promo-pikachu-summer', 'web-surfing-pikachu'],
  },
  {
    slug: 'web-surfing-pikachu',
    itemId: 821,
    categoryId: 1912,
    nameJa: 'なみのりピカチュウ',
    nameEn: 'Surfing Pikachu',
    h1: 'なみのりピカチュウ',
    title: 'なみのりピカチュウ（ポケモンカード★web）の販売 | HOLIC',
    description: 'ポケモンカード★webのなみのりピカチュウ。アンコモン、HP50、雷のたね。イラストはToshinao Aoki、ワザはなみのり。このカードの販売出品を案内します。',
    intro: 'ポケモンカード★webのなみのりピカチュウは、アンコモンでHP50です。Toshinao Aokiが描き、ワザはダメージ30のなみのりだけ。雷タイプのたねポケモンとしてカタログに入っています。',
    set: { id: 1912, nameJa: 'ポケモンカード★web' },
    setReleaseDate: '2001-10-20',
    rarityJa: 'アンコモン',
    hp: '50',
    typesJa: ['雷'],
    stageJa: 'たね',
    artist: 'Toshinao Aoki',
    artistEn: 'Toshinao Aoki',
    attacks: [{ name: 'なみのり', damage: '30', text: null }],
    abilities: [],
    editorial: false,
    group: 'promo',
    relatedSlugs: ['promo-pikachu-toyota', 'promo-pikachu-summer'],
  },
  {
    slug: 'neo-umbreon',
    itemId: 616,
    categoryId: 2003,
    nameJa: 'ブラッキー',
    nameEn: 'Umbreon',
    h1: 'ブラッキー（遺跡をこえて...）',
    title: 'ブラッキー（遺跡をこえて...）の販売 | HOLIC',
    description: '『遺跡をこえて...』のブラッキー。HP80、レベル40、レア、悪タイプの1進化。イラストはKimiya Masago。ワザはかみつくとだましうち。販売価格は出品者の提示です。',
    intro: '拡張パック第2弾『遺跡をこえて...』のブラッキーです。レベル40、HP80、レア。イラストレーターはKimiya Masago。ワザはかみつく（ダメージ20）と、相手の場のポケモン1匹に30ダメージを通すだましうちです。',
    set: { id: 2003, nameJa: '拡張パック第2弾 遺跡をこえて...' },
    series: { id: 1910, nameJa: 'ポケモンカード★neo' },
    setReleaseDate: '2000-07-07',
    rarityJa: 'レア',
    hp: '80',
    level: '40',
    typesJa: ['悪'],
    stageJa: '1進化',
    artist: 'Kimiya Masago',
    artistEn: 'Kimiya Masago',
    attacks: [
      { name: 'かみつく', damage: '20', text: null },
      {
        name: 'だましうち',
        damage: null,
        text: '相手の場のポケモンを1匹選び、そのポケモンに、「弱点・抵抗力・特殊能力・その他の効果」に関係なく、30ダメージ。',
      },
    ],
    abilities: [],
    flavorText: '近よるものを恐れさせるため、体のわっかもようは暗くなると自然に光りだす。',
    editorial: true,
    group: 'market',
    relatedSlugs: ['eevee-heroes-umbreon-vmax', 'neo-lugia'],
  },
  {
    slug: 'eevee-heroes-umbreon-vmax',
    itemId: 4842,
    categoryId: 2255,
    nameJa: 'ブラッキーVMAX',
    nameEn: 'Umbreon VMAX',
    h1: 'ブラッキーVMAX（イーブイヒーローズ）',
    title: 'ブラッキーVMAX（イーブイヒーローズ）の販売 | HOLIC',
    description: 'イーブイヒーローズのブラッキーVMAX（2021年5月28日発売）。イラストレーターとワザはカタログ未登録です。販売出品があるとき、その価格だけを相場として扱います。',
    intro: '強化拡張パック イーブイヒーローズ（2021年5月28日）のブラッキーVMAXです。カタログにはイラストレーターもワザも特性もまだ入っていません。セット名と発売日、そして実際に付いている販売出品だけを案内します。SARという区分では登録されていません。',
    set: { id: 2255, nameJa: '強化拡張パック イーブイヒーローズ' },
    series: { id: 1923, nameJa: 'ポケモンカードゲーム ソード&シールド' },
    releaseDate: '2021-05-28',
    typesJa: [],
    attacks: [],
    abilities: [],
    editorial: true,
    group: 'market',
    relatedSlugs: ['neo-umbreon', 'volt-tackle-pikachu-vmax'],
  },
  {
    slug: 'volt-tackle-pikachu-vmax',
    itemId: 10963,
    categoryId: 2248,
    nameJa: 'ピカチュウVMAX',
    nameEn: 'Pikachu VMAX',
    h1: 'ピカチュウVMAX（仰天のボルテッカー）',
    title: 'ピカチュウVMAX（仰天のボルテッカー）の販売 | HOLIC',
    description: '仰天のボルテッカーのピカチュウVMAX（2020年9月18日発売）。ワザとイラストレーターは未登録なので書きません。販売一覧で、出品者が付けた価格を確認できます。',
    intro: '拡張パック 仰天のボルテッカー、2020年9月18日発売のピカチュウVMAXです。このレコードにワザ・HP・イラストレーターは入っていません。同パックに別IDがあっても、販売リンクはこの収録のピカチュウVMAXに固定しています。',
    set: { id: 2248, nameJa: '拡張パック 仰天のボルテッカー' },
    series: { id: 1923, nameJa: 'ポケモンカードゲーム ソード&シールド' },
    releaseDate: '2020-09-18',
    typesJa: [],
    attacks: [],
    abilities: [],
    editorial: false,
    group: 'market',
    relatedSlugs: ['promo-pikachu-summer', 'eevee-heroes-umbreon-vmax'],
  },
  {
    slug: 'base-mewtwo-1st',
    itemId: 154,
    categoryId: 5,
    nameJa: 'ミュウツー',
    nameEn: 'Mewtwo',
    h1: 'ミュウツー（初版）',
    title: 'ミュウツー（初版・第1弾拡張パック）の販売 | HOLIC',
    description: '初版の第1弾ミュウツー。超タイプのたね、HP60、レベル53、レア。イラストは杉森建、ワザはサイコキネシスとバリアー。旧裏面のミュウツーとは分けた販売ページです。',
    intro: '初版の第1弾拡張パックに入るミュウツーです。杉森建、HP60、レベル53、レア。ワザはサイコキネシスとバリアー。旧裏面のミュウツーとは初版かどうかで分けています。',
    set: { id: 3, nameJa: '第1弾拡張パック' },
    printing: { id: 5, nameJa: '初版' },
    series: { id: 2, nameJa: '1番目のシリーズ' },
    setReleaseDate: '1996-10-15',
    rarityJa: 'レア',
    hp: '60',
    level: '53',
    typesJa: ['超'],
    stageJa: 'たね',
    artist: '杉森建',
    artistEn: 'Ken Sugimori',
    attacks: [
      { name: 'サイコキネシス', damage: '10+', text: '相手についているエネルギーカード×10の追加ダメージをあたえる。' },
      { name: 'バリアー', damage: null, text: '自分についている「超」エネルギーカードを1枚はがしてすてることによって、次の相手の番の「ワザ」をうけない。' },
    ],
    abilities: [],
    flavorText: '研究ために遺伝子をどんどん組み替えていった結果、狂暴なポケモンになった。',
    editorial: false,
    group: 'classic',
    relatedSlugs: ['base-charizard-1st', 'fossil-gengar'],
  },
  {
    slug: 'fossil-gengar',
    itemId: 298,
    categoryId: 35,
    nameJa: 'ゲンガー',
    nameEn: 'Gengar',
    h1: 'ゲンガー（化石の秘密）',
    title: 'ゲンガー（化石の秘密）の販売 | HOLIC',
    description: '第3弾『化石の秘密』のゲンガー。HP80、レベル38、レア、超タイプの2進化。イラストは杵淵敬二。特性はのろい、ワザはナイトヘッド。このカードの販売ページです。',
    intro: '第3弾拡張パック 化石の秘密のゲンガーは、レベル38、HP80の2進化です。杵淵敬二のイラスト。特性のろいと、ワザのナイトヘッドを持ちます。レアリティはレアです。',
    set: { id: 35, nameJa: '第3弾拡張パック 化石の秘密' },
    series: { id: 2, nameJa: '1番目のシリーズ' },
    setReleaseDate: '1997-06-21',
    rarityJa: 'レア',
    hp: '80',
    level: '38',
    typesJa: ['超'],
    stageJa: '2進化',
    artist: '杵淵敬二',
    artistEn: 'Keiji Kinebuchi',
    attacks: [{
      name: 'ナイトヘッド',
      damage: '30',
      text: '相手の控えポケモンから1匹選び、「弱点・抵抗力」に関係なく10ダメージ。相手に控えポケモンがいない時には、この効果はない。',
    }],
    abilities: [{
      name: 'のろい',
      text: '自分の番ごとに1回だけ使える。相手の場のポケモンにのっているダメージカウンターを1つ選び、相手の場の別のポケモンの上にのせてよい。「ねむり・マヒ・こんらん」状態の時、この力は使えない。',
    }],
    flavorText: '山で遭難したときに、命をうばいに暗闇から現れることがあるという。',
    editorial: false,
    group: 'classic',
    relatedSlugs: ['base-mewtwo-1st', 'dark-charizard'],
  },
  {
    slug: 'neo-lugia',
    itemId: 554,
    categoryId: 2002,
    nameJa: 'ルギア',
    nameEn: 'Lugia',
    h1: 'ルギア（金、銀、新世界へ...）',
    title: 'ルギア（金、銀、新世界へ...）の販売 | HOLIC',
    description: '『金、銀、新世界へ...』のルギア。無色のたね、HP90、レベル45、レア。イラストはHironobu Yoshida、ワザはエレメンタルブラスト。出品者の販売を見ます。',
    intro: '拡張パック第1弾『金、銀、新世界へ...』のルギアです。基本ポケモンでHP90、レベル45、レア。Hironobu Yoshidaの絵で、ワザはエレメンタルブラストです。無色タイプとして登録されています。',
    set: { id: 2002, nameJa: '拡張パック第1弾 金、銀、新世界へ...' },
    series: { id: 1910, nameJa: 'ポケモンカード★neo' },
    setReleaseDate: '1999-12-10',
    rarityJa: 'レア',
    hp: '90',
    level: '45',
    typesJa: ['無色'],
    stageJa: 'たね',
    artist: 'Hironobu Yoshida',
    artistEn: 'Hironobu Yoshida',
    attacks: [{
      name: 'エレメンタルブラスト',
      damage: '90',
      text: '自分についている「炎」「水」「雷」の「エネルギーカード」を、それぞれ1枚ずつトラッシュする。トラッシュできないとき、このワザのダメージはなくなる。',
    }],
    abilities: [],
    flavorText: '強すぎる能力を持つため、深い海の底で静かにときを過ごすと伝えられる。',
    editorial: false,
    group: 'classic',
    relatedSlugs: ['neo-umbreon', 'shining-charizard'],
  },
  {
    slug: 'gym-blaines-charizard',
    itemId: 1499,
    categoryId: 1924,
    nameJa: 'カツラのリザードン',
    nameEn: "Blaine's Charizard",
    h1: 'カツラのリザードン',
    title: 'カツラのリザードン（闇からの挑戦）の販売 | HOLIC',
    description: '闇からの挑戦のカツラのリザードン。HP100、レベル50、炎の2進化。イラストは杉森建、ワザはファイヤーブレスとかえんひこう。第1弾リザードンとは別カードです。',
    intro: 'ジム拡張第2弾 闇からの挑戦のカツラのリザードンです。レベル50、HP100。杉森建が描き、ワザはファイヤーブレスとかえんひこうです。第1弾のリザードンとはカード名も収録パックも別です。',
    set: { id: 1924, nameJa: 'ジム拡張第2弾 闇からの挑戦' },
    series: { id: 2, nameJa: '1番目のシリーズ' },
    setReleaseDate: '1999-06-25',
    rarityJa: 'レア',
    hp: '100',
    level: '50',
    typesJa: ['炎'],
    stageJa: '2進化',
    artist: '杉森建',
    artistEn: 'Ken Sugimori',
    attacks: [
      {
        name: 'ファイヤーブレス',
        damage: '20+',
        text: 'このワザに必要なエネルギーよりも多く「炎」エネルギーがついている時、多い「炎」エネルギーをすべてトラッシュして、トラッシュしたエネルギーの数×20のダメージを追加する。',
      },
      {
        name: 'かえんひこう',
        damage: null,
        text: 'コインを投げて「おもて」なら、相手の場のポケモンを1匹選び、「弱点・抵抗力」に関係なく、40ダメージ。',
      },
    ],
    abilities: [],
    editorial: false,
    group: 'classic',
    relatedSlugs: ['dark-charizard', 'base-charizard-unlimited'],
  },
  {
    slug: 'anniversary-mew-ex',
    itemId: 30424,
    categoryId: 3011,
    nameJa: 'ミュウex',
    nameEn: 'Mew ex',
    h1: 'ミュウex（25th ANNIVERSARY edition）',
    title: 'ミュウex（25th ANNIVERSARY edition）の販売 | HOLIC',
    description: '25th ANNIVERSARY editionのミュウex（2021年10月22日発売）。イラストとワザは未登録です。パック名と発売日、そして販売中の出品だけを案内します。',
    intro: 'プロモカードパック 25th ANNIVERSARY edition（2021年10月22日）のミュウexです。イラストレーターとワザはカタログ未登録なので書きません。発売日とパック名、販売中の出品だけがこのページの中身です。',
    set: { id: 3011, nameJa: 'プロモカードパック 25th ANNIVERSARY edition' },
    series: { id: 1923, nameJa: 'ポケモンカードゲーム ソード&シールド' },
    releaseDate: '2021-10-22',
    typesJa: [],
    attacks: [],
    abilities: [],
    editorial: false,
    group: 'promo',
    relatedSlugs: ['volt-tackle-pikachu-vmax', 'eevee-heroes-umbreon-vmax'],
  },
]

export const priorityCardByItemId = (itemId: number): PriorityCard | undefined => {
  return PRIORITY_CARDS.find((card) => card.itemId === itemId)
}

export const priorityCardBySlug = (slug: string): PriorityCard | undefined => {
  return PRIORITY_CARDS.find((card) => card.slug === slug)
}
